/**
 * Live 3D material preview for the Material Browser.
 *
 * Renders the classic BIM-style sphere + box sample with the appearance
 * asset applied as a real PBR material: diffuse color, roughness, metalness,
 * transparency, image texture (tiling by tileSize), procedural bump derived
 * from the material's fill pattern, and self-illumination. Updates instantly
 * while editing any tab.
 */

import { useEffect, useMemo } from 'react';
import { Canvas, useThree } from '@react-three/fiber';
import { OrbitControls } from '@react-three/drei';
import * as THREE from 'three';
import { RoomEnvironment } from 'three/examples/jsm/environments/RoomEnvironment.js';
import { Box, Text, VStack } from '@chakra-ui/react';
import {
    AppearanceAsset,
    BIMMaterial,
    FillPattern,
    getFillPattern,
} from '../../../core/MaterialTypes';
import { appearanceTexture } from '../../../rendering/MaterialResolver';
import { useImageLibraryStore } from '../../../state/useImageLibraryStore';

/** Local PMREM environment so metals/roughness read correctly without HDR files. */
function PbrEnvironment() {
    const gl = useThree((state) => state.gl);
    const scene = useThree((state) => state.scene);
    useEffect(() => {
        const pmrem = new THREE.PMREMGenerator(gl);
        const room = new RoomEnvironment();
        const envTexture = pmrem.fromScene(room, 0.04).texture;
        scene.environment = envTexture;
        return () => {
            scene.environment = null;
            envTexture.dispose();
            pmrem.dispose();
        };
    }, [gl, scene]);
    return null;
}

const BUMP_SIZE = 256;

/**
 * Bake the material's fill pattern into a grayscale height map used as the
 * bump texture (joints read darker, raised areas lighter), so the preview
 * shows the relief feel even without textures.
 */
function makePatternBumpCanvas(pattern: FillPattern | undefined): HTMLCanvasElement {
    const canvas = document.createElement('canvas');
    canvas.width = BUMP_SIZE;
    canvas.height = BUMP_SIZE;
    const ctx = canvas.getContext('2d');
    if (!ctx) return canvas;

    // Neutral mid-gray base = flat surface.
    ctx.fillStyle = '#808080';
    ctx.fillRect(0, 0, BUMP_SIZE, BUMP_SIZE);

    const style = pattern?.style ?? 'None';
    const lines = '#484848';
    const raised = '#b4b4b4';

    if (style === 'Solid') {
        ctx.fillStyle = '#9a9a9a';
        ctx.fillRect(0, 0, BUMP_SIZE, BUMP_SIZE);
        return canvas;
    }

    if (style === 'None') {
        // Subtle stipple noise so bumpStrength still has a visible effect.
        ctx.fillStyle = lines;
        for (let i = 0; i < 140; i++) {
            const x = Math.random() * BUMP_SIZE;
            const y = Math.random() * BUMP_SIZE;
            ctx.fillRect(x, y, 2, 2);
        }
        return canvas;
    }

    // Pattern repeat count in a ~0.5 m canvas tile.
    const count = Math.max(2, Math.min(24, Math.round(0.5 / Math.max(0.002, pattern?.spacing ?? 0.01))));
    const cell = BUMP_SIZE / count;
    const lw = Math.max(1.5, cell * (pattern?.thickness ?? 0.08) * 4);

    ctx.lineWidth = lw;
    ctx.strokeStyle = lines;

    if (style === 'Parallel' || style === 'ANSI31' || style === 'Crosshatch') {
        ctx.save();
        ctx.translate(BUMP_SIZE / 2, BUMP_SIZE / 2);
        ctx.rotate(-((pattern?.angle ?? 45) * Math.PI) / 180);
        for (let p = -BUMP_SIZE; p <= BUMP_SIZE * 2; p += cell) {
            ctx.beginPath();
            ctx.moveTo(p, -BUMP_SIZE);
            ctx.lineTo(p, BUMP_SIZE * 2);
            ctx.stroke();
        }
        if (style === 'Crosshatch') {
            ctx.rotate(Math.PI / 2);
            for (let p = -BUMP_SIZE; p <= BUMP_SIZE * 2; p += cell) {
                ctx.beginPath();
                ctx.moveTo(p, -BUMP_SIZE);
                ctx.lineTo(p, BUMP_SIZE * 2);
                ctx.stroke();
            }
        }
        ctx.restore();
    } else if (style === 'Brick' || style === 'Blocks') {
        // Courses with staggered vertical joints.
        for (let y = 0; y <= BUMP_SIZE; y += cell) {
            ctx.beginPath();
            ctx.moveTo(0, y);
            ctx.lineTo(BUMP_SIZE, y);
            ctx.stroke();
        }
        const row = Math.round(BUMP_SIZE / cell);
        for (let r = 0; r <= row; r++) {
            const y = r * cell;
            const shift = r % 2 === 0 ? 0 : cell / 2;
            for (let x = shift; x <= BUMP_SIZE; x += cell * (style === 'Brick' ? 2 : 1)) {
                ctx.beginPath();
                ctx.moveTo(x, y);
                ctx.lineTo(x, y + cell);
                ctx.stroke();
            }
        }
        // Face relief highlight.
        ctx.strokeStyle = raised;
        ctx.lineWidth = lw * 0.5;
        for (let r = 0; r <= row; r++) {
            ctx.beginPath();
            ctx.moveTo(0, r * cell + lw);
            ctx.lineTo(BUMP_SIZE, r * cell + lw);
            ctx.stroke();
        }
    } else if (style === 'Dots') {
        ctx.fillStyle = lines;
        for (let y = cell / 2; y < BUMP_SIZE; y += cell) {
            for (let x = cell / 2; x < BUMP_SIZE; x += cell) {
                ctx.beginPath();
                ctx.arc(x, y, lw * 0.9, 0, Math.PI * 2);
                ctx.fill();
            }
        }
    }

    return canvas;
}

/** One preview surface; rendered once per sample mesh. */
function previewMaterialProps(
    asset: AppearanceAsset | undefined,
    mapTex: THREE.Texture | undefined,
    bumpTex: THREE.Texture | undefined,
) {
    const transparency = asset?.transparency ?? 0;
    return {
        color: asset?.color ?? '#cccccc',
        roughness: asset?.roughness ?? 0.9,
        metalness: asset?.metalness ?? 0,
        transparent: transparency > 0,
        opacity: 1 - transparency,
        map: mapTex,
        bumpMap: bumpTex,
        bumpScale: (asset?.bumpStrength ?? 0) * 0.12,
        emissive: asset?.emissionColor || '#000000',
        emissiveIntensity: asset?.emissionStrength ?? 0,
        envMapIntensity: 1.0,
    } as const;
}

export function MaterialLivePreview({
    material,
    asset,
}: {
    material: BIMMaterial;
    asset: AppearanceAsset | undefined;
}) {
    const images = useImageLibraryStore((state) => state.images);

    const imageUrl = asset?.imageId
        ? images.find((img) => img.id === asset.imageId)?.url
        : undefined;

    const mapTexture = useMemo(
        () => (imageUrl ? appearanceTexture(imageUrl, asset?.tileSize ?? 1) : undefined),
        [imageUrl, asset?.tileSize],
    );

    const bumpPattern = getFillPattern(
        material.graphics.surfacePatternId !== 'fp-none'
            ? material.graphics.surfacePatternId
            : material.graphics.cutPatternId,
    );

    const bumpTexture = useMemo(() => {
        if ((asset?.bumpStrength ?? 0) <= 0) return undefined;
        const tex = new THREE.CanvasTexture(makePatternBumpCanvas(bumpPattern));
        tex.wrapS = THREE.RepeatWrapping;
        tex.wrapT = THREE.RepeatWrapping;
        // One canvas tile ≈ 0.5 m of surface; align with the tile size feel.
        const tile = asset?.tileSize ?? 1;
        tex.repeat.set(1 / Math.max(0.05, tile * 2), 1 / Math.max(0.05, tile * 2));
        return tex;
    }, [bumpPattern, asset?.bumpStrength, asset?.tileSize]);

    // Dispose the procedural bump map when it is regenerated or unmounted.
    useEffect(() => () => bumpTexture?.dispose(), [bumpTexture]);

    const props = previewMaterialProps(asset, mapTexture, bumpTexture);

    return (
        <Box
            display="flex"
            flexDirection="column"
            height="full"
            minW="170px"
            flexShrink={0}
            pl={3}
            py={2}
            pr={1}
            borderLeftWidth="1px"
            borderLeftColor="var(--color-border)"
            borderLeftStyle="solid"
        >
            <Text
                fontSize="2xs"
                fontWeight="semibold"
                textTransform="uppercase"
                letterSpacing="0.06em"
                color="var(--color-text-muted)"
                px={2}
                pb={1}
            >
                Live Preview
            </Text>
            <Box
                flex="1"
                minH="220px"
                borderRadius="var(--cad-radius-md)"
                overflow="hidden"
                borderWidth="1px"
                borderColor="var(--color-border)"
                bg="var(--color-bg-viewport)"
            >
                <Canvas
                    dpr={[1, 1.5]}
                    camera={{ position: [1.35, 0.75, 1.55], fov: 34 }}
                    gl={{ antialias: true, alpha: true }}
                >
                    <PbrEnvironment />
                    <ambientLight intensity={0.25} />
                    <directionalLight position={[2.2, 3, 2]} intensity={0.7} />
                    <directionalLight position={[-2, 1.2, -1.5]} intensity={0.25} />

                    {/* Box sample (BIM asset preview convention) */}
                    <mesh position={[-0.36, -0.16, -0.08]} rotation={[0, 0.55, 0]}>
                        <boxGeometry args={[0.58, 0.58, 0.58]} />
                        <meshPhysicalMaterial {...props} />
                    </mesh>

                    {/* Sphere sample in front */}
                    <mesh position={[0.34, 0.06, 0.16]}>
                        <sphereGeometry args={[0.32, 48, 32]} />
                        <meshPhysicalMaterial {...props} />
                    </mesh>

                    {/* Faint ground disc for grounding the samples */}
                    <mesh position={[0, -0.5, 0]} rotation={[-Math.PI / 2, 0, 0]}>
                        <circleGeometry args={[1.15, 48]} />
                        <meshStandardMaterial color="#2d2d30" roughness={0.95} metalness={0} />
                    </mesh>

                    <OrbitControls
                        autoRotate
                        autoRotateSpeed={1.4}
                        enableZoom={false}
                        enablePan={false}
                        enableDamping
                        dampingFactor={0.08}
                        target={[0, -0.05, 0]}
                        minPolarAngle={0.6}
                        maxPolarAngle={1.9}
                    />
                </Canvas>
            </Box>

            {/* Compact readout under the viewport */}
            <VStack spacing={0} align="start" px={2} pt={1.5}>
                <Text fontSize="xs" noOfLines={1} title={material.name}>
                    {material.name}
                </Text>
                <Text fontSize="2xs" color="var(--color-text-muted)">
                    R {Math.round((asset?.roughness ?? 0.9) * 100)}% · M {Math.round((asset?.metalness ?? 0) * 100)}%
                    {(asset?.transparency ?? 0) > 0
                        ? ` · T ${Math.round((asset?.transparency ?? 0) * 100)}%`
                        : ''}
                    {(asset?.bumpStrength ?? 0) > 0 ? ' · bump' : ''}
                    {mapTexture ? ' · texture' : ''}
                </Text>
            </VStack>
        </Box>
    );
}
